<template>
    <inline-tab-panel
        name="comments"
        v-bind="$attrs"
        class="comments-tab-panel"
        with-variable-height
    >
        <flex-container align-items="center" :gap="Size.sm">
            <user-avatar
                :placeholder="currentUser?.name.abbreviation ?? ''"
                :url="currentUser?.avatar ? resizedToSizeUploadUrl(currentUser?.avatar, IconSize.lg) : ''"
                :alt="currentUser?.name.fullName"
            />
            <text-decorator bold>
                {{ currentUser?.name.fullName }}
            </text-decorator>
        </flex-container>

        <br>

        <input-group v-model="newComment">
            <template #input>
                <rich-text-area-input :mentionables="mentionables" />
            </template>
        </input-group>

        <flex-container
            align-items="center"
            justify-content="flex-end"
            no-wrap
        >
            <input-group v-model="commentVisibility" inline>
                <template #input>
                    <comment-visibility-input />
                </template>
            </input-group>
            <button
                type="button"
                class="add-comment-button"
                :disabled="!newComment || newComment === '<p><br></p>'"
                @click="createComment"
            >
                <svg-icon
                    :icon="IconSend"
                    is-custom-icon
                />
            </button>
        </flex-container>

        <div v-if="showAutoCloseNotification" class="comments-tab-panel--autoCloseComment">
            <comment-block
                hide-footer
                pinned
                :pin-color="Color.orange500"
                :color="Color.orange100"
                :comment="autoCloseComment"
            />
        </div>

        <template v-for="(commentsForDate, date) in groupedComments" :key="date">
            <template v-if="date === firstDate && !showAutoCloseNotification">
                <div class="comments-tab-panel__date">
                    <chip-informative>
                        {{ date }}
                    </chip-informative>
                </div>
            </template>
            <template v-else>
                <div class="comments-tab-panel__date comments-tab-panel__date--striped">
                    <div style="background-color: white; display: inline-block; position: relative; ">
                        <chip-informative>
                            {{ date }}
                        </chip-informative>
                    </div>
                </div>
            </template>

            <div
                v-for="(comment, index) in commentsForDate"
                :key="comment.id.toString()"
            >
                <horizontal-line v-if="index !== 0" />
                <comment-block
                    :comment="comment"
                    :delete-comment="deleteComment"
                />
            </div>
        </template>
    </inline-tab-panel>
</template>

<script setup lang="ts">
    import {
        InlineTabPanel,
        TextDecorator,
        FlexContainer,
        UserAvatar,
        RichTextAreaInput,
        HorizontalLine,
        InputGroup,
        SvgIcon,
        IconSize,
        Size,
        Color,
        ChipInformative,
    } from '@keyvue/one-ui';

    import {
        CompanyAPI,
        CommentAPI,
        createResourceIdentifier,
        Comment as LegacyComment,
        PipelineAPI,
    } from '@keyvue/one-api-client-legacy';

    import { v4 as uuid } from 'uuid';
    import CommentBlock from '@components/Blocks/CommentBlock.vue';
    import IconSend from '@assets/icons/Send.svg?component';
    import { computed, ref } from 'vue';
    import { Comment, CommentOrigin, type User } from '@keyvue/one-api-client';
    import type { ReactiveCollection } from '@composables/useClient';
    import CommentVisibilityInput from '@components/Inputs/CommentVisibilityInput.vue';
    import { CommentId, CommentVisibility, Lead } from '@keyvue/one-api-client';
    import useClient from '@composables/useClient';
    import { formatDayMonth } from '@/utils/date';
    import { resizedToSizeUploadUrl } from '@/utils/resize';
    import useMentionable from '@composables/useMentionable';
    import useNotify from '@composables/useNotify';
    import useEventBus from '@composables/useEventBus';

    interface Props {
        lead: Lead
        commentCollection: ReactiveCollection<Comment>
        mentionableUserCollection: User[]
    }

    const props = defineProps<Props>();
    const { currentUser } = useClient();
    const { notifySaving, notifySaved, notifyError } = useNotify();
    const eventBus = useEventBus();

    const availableComments = computed<Comment[]>(() => props.commentCollection.data.value);

    const availableMentionableUsers = computed<User[]>(() => props.mentionableUserCollection ?? []);
    const mentionable = useMentionable(availableMentionableUsers);
    const mentionables = computed(() => mentionable.mentionables.value);

    const newComment = ref<string>('');
    const commentVisibility = ref<{ value: CommentVisibility, label: string }>(CommentVisibility.options[0]);

    const groupedComments = computed(() => {
        const commentsByDate = {};
        const localComments = availableComments.value;

        localComments.sort((a, b) => +b.createdAt - +a.createdAt);
        localComments.forEach((comment) => {
            const date = comment.createdAt;
            const formattedDate = formatDayMonth(date);

            if (!(formattedDate in commentsByDate)) {
                commentsByDate[formattedDate] = [];
            }

            commentsByDate[formattedDate].push(comment);
        });

        return commentsByDate as Record<string, Comment[]>;
    });

    const firstDate = computed(() => (Object.keys(groupedComments.value)[0]));

    const addComment = async () => {
        if (!currentUser?.value) {
            throw new Error('No current user');
        }

        const closeNotification = notifySaving(undefined, 0);
        try {
            const newLegacyComment = LegacyComment.fromData({
                referral: createResourceIdentifier('referrals', props.lead.id.value),
                user: createResourceIdentifier('users', currentUser.value.id.value),
                id: uuid(),
                comment: newComment.value,
                origin: CommentOrigin.USER,
                visibility: commentVisibility.value.value.value,
            });

            await CommentAPI.create(newLegacyComment);
            await mentionable.mention(newLegacyComment);
            eventBus.dispatch('resource-manipulated', 'Comment');
            notifySaved();
        } catch (error) {
            notifyError(error);
        } finally {
            if (closeNotification) {
                closeNotification();
            }
        }
    };

    const deleteComment = async (commentId: CommentId) => {
        const closeNotification = notifySaving(undefined, 0);
        await CommentAPI.delete(commentId.value);
        eventBus.dispatch('resource-manipulated', 'Comment');
        notifySaved();
        if (closeNotification) {
            closeNotification();
        }
    };

    const createComment = async () => {
        if (!addComment) {
            return;
        }

        await addComment();
        newComment.value = '';
    };

    const assignedLegacyCompany = await CompanyAPI.getForOneApp(props.lead.assignee.company.id.toString());
    const legacyPipeline = await PipelineAPI.get(props.lead.pipeline.id.toString());
    const notificationBeforeDays = computed(() => assignedLegacyCompany.autoCloseSettings.notificationBeforeDays);
    const closeAfterDays = computed(() => {
        let autoCloseAfterDays = assignedLegacyCompany.autoCloseSettings.closeAfterDays;
        if (legacyPipeline.autoCloseAfterDays !== null && legacyPipeline.autoCloseAfterDays !== undefined) {
            autoCloseAfterDays = legacyPipeline.autoCloseAfterDays;
        }
        return autoCloseAfterDays;
    });
    const closesInDays = computed(() => closeAfterDays.value - props.lead.daysInactive);

    const maxInactiveDays = computed(() => closeAfterDays.value - notificationBeforeDays.value);
    const autoCloseEnabled = computed(() => {
        if (!assignedLegacyCompany.autoCloseSettings.closeEnabled) {
            return false;
        }

        if (assignedLegacyCompany.autoCloseSettings.perPipelineEnabled) {
            return legacyPipeline.autoCloseAfterDays !== null && legacyPipeline.autoCloseAfterDays !== undefined;
        }

        return true;
    });
    const showAutoCloseNotification = computed(() => autoCloseEnabled.value && props.lead.daysInactive > maxInactiveDays.value);

    const autoCloseComment = Comment.fromObject({
        id: 'autoClose',
        comment: `
            Even een snelle heads-up: onze KeyVue beheerder heeft het zo geregeld dat leads
            die ${closeAfterDays.value} dagen stil liggen automatisch worden afgesloten. Deze
            specifieke lead staat op het punt om over ${closesInDays.value} dagen te sluiten als we niet in actie
            komen. Maar maak je geen zorgen, dit is makkelijk te voorkomen! Je hoeft alleen
            maar een kleine update te geven in de notities of een nieuwe afspraak of taak te
            plannen, dan reset de teller zich. Zo houden we alles mooi op koers en bijgewerkt.`,
        origin: CommentOrigin.VIRTUAL_COLLEAGUE.value,
        visibility: CommentVisibility.PUBLIC.value,
    }) as Comment;
</script>

<style scoped lang="scss">
.comments-tab-panel--autoCloseComment {
    margin-top: size(md);
}

.comments-tab-panel {
    padding: size(md) !important;

    &__date {
        position: relative;
        margin-bottom: size(md);
        text-align: center;
        padding-top: size(md);

        &::before {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            height: 1px;
            background-color: color(gray-300);
        }

        &--striped::before {
            top: size(lg) + 1px;
        }
    }
}

.add-comment-button {
    cursor: pointer;
    transition: transform .1s;
    border: 0;
    background: none;
    color: color(brand-green);

    &:hover {
        transform: translateX(5px);
    }

    &:disabled {
        color: color(gray-300);
    }
}
</style>
